import { isValidNumber } from "libphonenumber-js";

export const isEmailValid = (email: string): boolean => {
  const trimmedEmail = email.trim();
  const emailReg = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return !!trimmedEmail.match(emailReg);
};

export const isPhoneValid = (phone: string): boolean => {
  return isValidNumber(phone, "US");
};

export interface ContactInfoValidation {
  validEmail: boolean;
  validPhone: boolean;
}

export const validateContactInfo = (
  communicationPreference: string,
  phoneNumber: string,
  email: string
): ContactInfoValidation => {
  return {
    validEmail: communicationPreference === "EMAIL" ? isEmailValid(email) : !email || isEmailValid(email),
    validPhone:
      communicationPreference === "TEXT" ? isPhoneValid(phoneNumber) : !phoneNumber || isPhoneValid(phoneNumber)
  };
};

function checkForCloseMatch(longString: string, shortString: string) {
  // too many false positives with very short strings
  if (shortString.length < 3) {
    return "";
  }

  // test if the shortString is in the string (so everything is fine)
  if (longString && longString.includes(shortString)) {
    return "";
  }

  // split the shortString string into two at each postion e.g. g|mail gm|ail gma|il gmai|l
  // and test that each half exists with one gap
  for (let i = 1; i <= shortString.length; i++) {
    const firstPart = shortString.substring(0, i);
    const secondPart = shortString.substring(i);

    // test for wrong letter
    const wrongLetterRegEx = new RegExp(`${firstPart}.${secondPart.substring(1)}`);
    if (wrongLetterRegEx.test(longString)) {
      return longString.replace(wrongLetterRegEx, shortString);
    }

    // test for extra letter
    const extraLetterRegEx = new RegExp(`${firstPart}.${secondPart}`);
    if (extraLetterRegEx.test(longString)) {
      return longString.replace(extraLetterRegEx, shortString);
    }

    // test for missing letter
    if (secondPart !== "mail") {
      const missingLetterRegEx = new RegExp(`${firstPart}{0}${secondPart}`);
      if (missingLetterRegEx.test(longString)) {
        return longString.replace(missingLetterRegEx, shortString);
      }
    }

    // test for switched letters
    const switchedLetters = [
      shortString.substring(0, i - 1),
      shortString.charAt(i),
      shortString.charAt(i - 1),
      shortString.substring(i + 1)
    ].join("");

    if (longString && longString.includes(switchedLetters)) {
      return longString.replace(switchedLetters, shortString);
    }
  }

  // if nothing was close, then there wasn't a typo
  return "";
}

function checkForDomainTypo(userEmail: string) {
  const domains = ["gmail", "hotmail", "outlook", "yahoo", "icloud", "mail", "zoho"];
  const [leftPart, rightPart] = userEmail.split("@");

  for (const domain of domains) {
    const result = checkForCloseMatch(rightPart, domain);
    if (result) {
      return `${leftPart}@${result}`;
    }
  }

  return "";
}

function checkForNameTypo(userEmail: string, name: string) {
  const [leftPart, rightPart] = userEmail.split("@");
  const result = checkForCloseMatch(leftPart, name);

  if (result) {
    return `${result}@${rightPart}`;
  }

  return "";
}

function checkForCommonTypos(userInput: string) {
  const commonTypos = [
    // the comment code is never hit because we don't consider ,com a valid
    // email address
    // {
    //   pattern: /,com$/,
    //   fix: (str: string) => str.replace(/,com$/, ".com")
    // },
    {
      pattern: /\.c$/,
      fix: (str: string) => str.replace(/\.c$/, ".com")
    },
    // {
    //   pattern: /,co\.\w{2}$/,
    //   fix: (str: string) => str.replace(/,(co\.\w{2}$)/, ".$1")
    // },
    {
      pattern: /@\w*$/,
      fix: (str: string) => str + ".com"
    }
  ];

  const typo = commonTypos.find(t => t.pattern.test(userInput));

  if (typo) {
    return typo.fix(userInput);
  }

  return "";
}

export function checkForTypo(firstName: string, lastName: string, email: string) {
  const normalizedEmail = email.trim().toLowerCase();

  let fixedEmail = "";
  fixedEmail = checkForCommonTypos(normalizedEmail);

  if (fixedEmail) {
    const temp = checkForDomainTypo(fixedEmail);
    if (temp) {
      fixedEmail = temp;
    }
  } else {
    fixedEmail = checkForDomainTypo(normalizedEmail);
  }

  if (firstName){
    if (fixedEmail) {
      const temp = checkForNameTypo(fixedEmail, firstName.trim().toLowerCase());
      if (temp) {
        fixedEmail = temp;
      }
    } else {
      fixedEmail = checkForNameTypo(normalizedEmail, firstName.trim().toLowerCase());
    }
  }

  if (lastName){
    if (fixedEmail) {
      const temp = checkForNameTypo(fixedEmail, lastName.trim().toLowerCase());
      if (temp) {
        fixedEmail = temp;
      }
    } else {
      fixedEmail = checkForNameTypo(normalizedEmail, lastName.trim().toLowerCase());
    }
  }
  return fixedEmail;
}
